import React from 'react';
import { MapPin, Clock, Info, Bike, ShoppingBag } from 'lucide-react';
import { Restaurant, RestaurantSettings } from '../../lib/types';
import { formatCurrency } from '../../lib/utils';
interface RestaurantHeaderProps {
  restaurant: Restaurant;
  settings: RestaurantSettings | null;
  zoneName?: string;
}
export function RestaurantHeader({
  restaurant,
  settings,
  zoneName
}: RestaurantHeaderProps) {
  return <div className="bg-[#0B0B0B] border-b border-white/10" data-id="element-392">
      {/* Cover Image */}
      <div className="relative h-44 sm:h-60 md:h-72 w-full bg-[#141414]" data-id="element-393">
        <img src={restaurant.cover_url || 'https://cdn.magicpatterns.com/uploads/rYDkPPUtvynEvWDL4HujUk/portada.png'} alt={restaurant.name} className="w-full h-full object-cover" data-id="element-394" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0B] via-black/40 to-transparent" data-id="element-395" />

        {/* Badges */}
        <div className="absolute top-4 right-4 flex flex-col gap-2 items-end" data-id="element-396">
          {restaurant.is_featured && <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-[#C9A34E] text-[#0B0B0B] shadow-lg" data-id="element-397">
              ★ Destacado
            </span>}
          {!restaurant.is_open && <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-[#CE1126] text-white shadow-lg" data-id="element-398">
              Cerrado
            </span>}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative" data-id="element-399">
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 sm:items-end -mt-10 sm:-mt-14 mb-5" data-id="element-400">
          {/* Logo */}
          <div className="relative w-20 h-20 sm:w-28 sm:h-28 rounded-2xl bg-[#0B0B0B] p-1 shadow-xl border border-white/10 shrink-0" data-id="element-401">
            {restaurant.logo_url ? <img src={restaurant.logo_url} alt={`${restaurant.name} logo`} className="w-full h-full rounded-xl object-cover" data-id="element-402" /> : <img src="https://cdn.magicpatterns.com/uploads/66K7i7HxpjApVZJyeR9rjp/ChatGPT_Image_15_mar_2026,_13_53_22.png" alt="NOIZA" className="w-full h-full rounded-xl object-cover" data-id="element-403" />}
          </div>

          {/* Title & Info */}
          <div className="flex-1 pb-2" data-id="element-404">
            <h1 className="text-2xl sm:text-4xl font-black text-white mb-1 tracking-tight" data-id="element-405">
              {restaurant.name}
            </h1>
            {restaurant.description_short && <p className="text-white/50 text-sm sm:text-base max-w-2xl mb-3" data-id="element-406">
                {restaurant.description_short}
              </p>}

            <div className="flex flex-wrap items-center gap-3 text-sm text-white/40 font-medium" data-id="element-407">
              {(zoneName || restaurant.address_text) && <div className="flex items-center gap-1.5" data-id="element-408">
                  <MapPin className="w-3.5 h-3.5" data-id="element-409" />
                  {zoneName || restaurant.address_text}
                </div>}

              {settings?.average_prep_time_minutes && <div className="flex items-center gap-1.5" data-id="element-410">
                  <Clock className="w-3.5 h-3.5" data-id="element-411" />
                  {settings.average_prep_time_minutes} min
                </div>}

              {settings?.minimum_order_amount != null && settings.minimum_order_amount > 0 && <div className="flex items-center gap-1.5" data-id="element-412">
                    <Info className="w-3.5 h-3.5" data-id="element-413" />
                    Mín. {formatCurrency(settings.minimum_order_amount)}
                  </div>}
            </div>
          </div>
        </div>

        {/* Order Methods — Mexican color gradient badges */}
        {settings && <div className="flex flex-wrap gap-2.5 pb-5" data-id="element-414">
            {settings.accept_delivery && <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold text-white border border-white/10" style={{
          background: 'linear-gradient(135deg, #006847 0%, #2AABB3 100%)'
        }} data-id="element-415">
                <Bike className="w-3.5 h-3.5" data-id="element-416" /> Delivery
              </span>}
            {settings.accept_pickup && <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold text-white border border-white/10" style={{
          background: 'linear-gradient(135deg, #CE1126 0%, #E8751A 100%)'
        }} data-id="element-417">
                <ShoppingBag className="w-3.5 h-3.5" data-id="element-418" /> Para llevar
              </span>}
          </div>}
      </div>
    </div>;
}